import React from 'react';

import { WidgetsMapComponentKeys } from 'config/widgetConfigurations/widgetsConfigurations';

import { useBaseTable } from 'hooks/useBaseTable';
import { useBaseWidget } from 'hooks/useBaseWidget';

import { QueryResponse } from 'api/queries/getRunRateAnalysisBreakdown.query';

import { ColumnsManager } from 'components/common/ColumnsManager/ColumnsManager';
import { ExportDropdown } from 'components/common/ExportDropdown/exportDropdown';
import NoData from 'components/common/NoData/noData';
import { TableV8 } from 'components/common/Tables/TableV8/tableV8';
import { TableContainer } from 'components/common/Tables/components/TableContainer/TableContainer';
import { WidgetTitle } from 'components/common/WidgetTitle/WidgetTitle';
import { WidgetWithTitle } from 'components/common/WidgetWithTitle/WidgetWithTitle';
import { SelectedRangeLabel } from 'components/common/selectedRangeLabel/selectedRangeLabel';

import classes from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/style/table.module.scss';
import { AddWidget } from 'components/Dashboards/Project/Components/CustomDashboard/AddRemoveWidget/addWidget';
import { PinboardWidgetPills } from 'components/Dashboards/Project/Components/CustomDashboard/PinboardWidgetPills/PinboardWidgetPills';
import { IComponentProps } from 'components/Dashboards/Project/Components/CustomDashboard/utils/utils.type';
import { ThreeDotsMenu } from 'components/Dashboards/Reporting/threeDotsMenu/ThreeDotsMenu';
import { useProductivityTableProvider } from 'components/widgets/RunRateActivitiesDrilldown/ProductivityTable.Provider';
import { useProductivityTableLogic } from 'components/widgets/RunRateActivitiesDrilldown/ProductivityTable.logic';
import { columnsV8, initialState } from 'components/widgets/RunRateActivitiesDrilldown/productivityTable.utils';

const id = 'productivityTable';
const title = 'Run Rate | Activities Drilldown';
const componentKey: WidgetsMapComponentKeys = 'productivityTable';

export const ProductivityTable = ({
    projectId,
    contractId,
    widgetId,
    latestVersionId,
    compareVersionId,
    globalFilterData,
    externalComponents = null,
    editNarrative = null,
    localFilters,
    setSubTitleForExport,
}: IComponentProps) => {
    const { table } = useBaseTable({
        widgetId,
        localFilters,
        componentKey,
        initialState,
        columns: columnsV8,
    });
    const { subTitle, route } = useBaseWidget({
        id,
        widgetId,
        projectId,
        contractId,
        latestVersionId,
        title,
        setSubTitleForExport,
    });

    const { ui, api } = useProductivityTableProvider({
        localFilters,
        globalFilterData,
        widgetId,
        latestVersionId,
        compareVersionId,
        table,
        componentKey,
    });
    const { logic } = useProductivityTableLogic({ ui, globalFilterData, table, api, latestVersionId, title, subTitle });

    return (
        <WidgetWithTitle
            id={id}
            tooltip={api.getRunRateAnalysisBreakdown.data?.info}
            title={
                <WidgetTitle
                    title={title}
                    postfix={
                        api.getRunRateAnalysisBreakdown.data?.numOfAllCards
                            ? `(${api.getRunRateAnalysisBreakdown.data?.numOfAllCards})`
                            : undefined
                    }
                />
            }
            titleFilters={[
                editNarrative && <div key={'editNarrative'}>{editNarrative}</div>,
                widgetId ? (
                    <PinboardWidgetPills key={'widgetFilters'} filters={ui.pills} />
                ) : (
                    <div className={classes.filters} key={'selectedRange'}>
                        <SelectedRangeLabel
                            selectedEndDate={ui.state.endDate}
                            selectedStartDate={ui.state.startDate}
                            seriesLabel={ui.state.selectedSeries}
                        />
                    </div>
                ),
            ]}
            titleComponents={[
                externalComponents && <div key={'externalComponents'}>{externalComponents}</div>,
                <AddWidget
                    key={'AddWidget'}
                    componentKey={componentKey}
                    title={title}
                    projectId={projectId}
                    contractId={contractId}
                    widgetId={widgetId}
                    route={route}
                    localFilters={ui.state}
                    callBack={table.setWidgetColumns}
                    shouldHide={true}
                />,
                <ExportDropdown
                    key={'export'}
                    title={title}
                    subTitle={subTitle}
                    handleAPICsvExport={logic.handleAPICsvExport}
                />,
                <ColumnsManager
                    onOpenModal={() => table.setModalColumnsOpen(true)}
                    onHideModal={() => table.setModalColumnsOpen(false)}
                    showModal={table.modalColumnsOpen}
                    widgetId={widgetId}
                    componentKey={componentKey}
                    tableInstance={table.tableInstance}
                    initialState={initialState}
                    columns={columnsV8}
                />,
                <ThreeDotsMenu widgetId={widgetId} componentKey={componentKey} shareTitle={title} shareLink={route} />,
            ]}
        >
            <TableContainer>
                {table.updatedInitialState && (
                    <TableV8<QueryResponse['cardList'][0]>
                        data={logic.filteredRows}
                        columns={columnsV8}
                        isLoading={
                            api.getRunRateAnalysisBreakdown.isLoading || api.getRunRateAnalysisBreakdown.isFetching
                        }
                        noData={<NoData>No activities match the applied filter</NoData>}
                        sorting={table.sorting}
                        setSorting={table.setSorting}
                        columnVisibility={table.columnVisibility}
                        setColumnVisibility={table.setColumnVisibility}
                        columnOrder={table.columnOrder}
                        setColumnOrder={table.setColumnOrder}
                        rowSelection={table.rowSelection}
                        setRowSelection={table.setRowSelection}
                        pagination={table.pagination}
                        totalPages={table.totalPages}
                        setPagination={table.setPagination}
                        isPaginationDisabled={api.getRunRateAnalysisBreakdown.isFetching}
                        displayPaginationBottomBar
                    />
                )}
            </TableContainer>
        </WidgetWithTitle>
    );
};
